import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo, useRef, useState } from 'react';
import { useDebounce } from 'rooks';
import { genericForwardRef } from '../../../helpers/genericForwardRef';
import { ComboboxInput } from '../ComboboxInput/ComboboxInput';
const WHISPERER_DEBOUNCE_IN_MS = 500;
const WHISPERER_MIN_LENGTH_TO_QUERY = 3;
const MAX_CACHE_SIZE = 10;
export const _Whisperer = ({ onWhisperSelect, getWhisperData, getOption, onValueChange, ...props }, ref) => {
    const [whispererResults, setWhisperResults] = useState([]);
    const lastSelectedValue = useRef('');
    const lastValueChanged = useRef('');
    const whispererResultsCache = useMemo(() => new Map(), []);
    const updateWhispererResults = (searchValue) => {
        // Hide combobox options and do not fire another getWhisperData when user selects option
        const isSameAsLastSelected = searchValue === lastSelectedValue.current;
        const isNotLongEnough = searchValue.length < WHISPERER_MIN_LENGTH_TO_QUERY;
        if (isSameAsLastSelected || isNotLongEnough) {
            setWhisperResults([]);
            return;
        }
        // Reset value when user starts typing again after option selection
        lastSelectedValue.current = '';
        const maybeCachedResults = whispererResultsCache.get(searchValue);
        if (maybeCachedResults) {
            // Move results at the end of cache
            whispererResultsCache.delete(searchValue);
            whispererResultsCache.set(searchValue, maybeCachedResults);
            setWhisperResults(maybeCachedResults);
            return;
        }
        getWhisperData(searchValue).then((results) => {
            const oldestKey = whispererResultsCache.keys().next().value;
            const shouldRemoveValueFromCache = whispererResultsCache.size >= MAX_CACHE_SIZE && !!oldestKey;
            if (shouldRemoveValueFromCache) {
                whispererResultsCache.delete(oldestKey);
            }
            whispererResultsCache.set(searchValue, results);
            setWhisperResults(results);
        });
    };
    const updateWhispererResultsDebounced = useDebounce(updateWhispererResults, WHISPERER_DEBOUNCE_IN_MS);
    const resetResults = () => {
        setWhisperResults([]);
        updateWhispererResultsDebounced('');
    };
    const handleValueChange = (changedValue) => {
        lastValueChanged.current = changedValue;
        updateWhispererResultsDebounced(changedValue);
        onValueChange?.(changedValue);
    };
    const options = whispererResults.map((whispererResult) => {
        const option = getOption(whispererResult);
        return option;
    });
    const onSelect = (selectedValue, selectedId) => {
        lastSelectedValue.current = selectedValue;
        const selectedOption = whispererResults.find((whispererResult) => {
            const option = getOption(whispererResult);
            if (option.id)
                return option.id === selectedId;
            return option.value === selectedValue;
        });
        resetResults();
        if (!selectedOption || !onWhisperSelect)
            return;
        onWhisperSelect(selectedOption);
    };
    const onFocus = (event) => {
        props.onFocus?.(event);
        const valueIsChanged = event.currentTarget.value !== lastValueChanged.current;
        if (valueIsChanged) {
            resetResults();
        }
    };
    return (_jsx(ComboboxInput, { ...props, ref: ref, options: options, onValueChange: handleValueChange, onSelect: onSelect, onFocus: onFocus }));
};
export const Whisperer = genericForwardRef(_Whisperer);
